/**
 * StudentActivityCorrection — la correction en attente puis prête, sur `<StudentCorrectionPage>` : le
 * moteur (`useCorrectionWait` : les relevés, les horloges, l'avis) et de quoi patienter (le défi
 * éclair, ou une pause tirée une fois). Toutes les sorties mènent aux félicitations (`onDone`).
 */
import React, { useMemo, useReducer, useState } from "react"

import { CorrectionChallenge, CorrectionPause, StudentCorrectionPage } from "../../../../../../design-system/student"
import { answerChallenge } from "../../../../../../engine/activity/correction/answerChallenge"
import {
    challengeReducer,
    NEW_GAME,
    pickCorrectionGame,
    playableQuestions,
    shownChoices,
    type ApiChallengeQuestion,
} from "../../../../../../engine/activity/correction/correctionChallenge"
import { submittedWorkImages } from "../../../../../../engine/activity/correction/correctionFlow"
import { FACTS, VIRELANGUES } from "../../../../../../engine/activity/correction/correctionPauses"
import {
    useCorrectionWait,
    type CorrectedActivity,
} from "../../../../../../engine/activity/correction/useCorrectionWait"
import { toCorrectedWork } from "../../../../adapters/student/Activity/studentActivityAdapter"
import { correctionFeedbackError } from "../../../../view-models/student/Activity/finishedActivityViewModel"
import { quizTextFormat } from "../../../../view-models/student/Activity/quizTextFormat"
import { trackCorrectionSkipped } from "./activityAnalytics"

export type StudentActivityCorrectionProps = {
    activity: CorrectedActivity
    questions: unknown[]
    onDone: () => void
    /** Le live : l'attente quittée avant la correction, qu'il annoncera à son arrivée. */
    onLeftUnsettled?: () => void
}

function Challenge({ questions, activityId }: { questions: ApiChallengeQuestion[]; activityId: number | null }) {
    const [game, play] = useReducer(challengeReducer, NEW_GAME)
    const question = questions[game.index]
    const choices = question != null ? shownChoices(question) : []
    const statement = question?.display_question || question?.question || ""
    const onAnswer = (index: number) => {
        if (question == null || game.selected !== null) return
        // Sans attendre le serveur : le jeu ne dépend pas de lui (`PendingAction.js`).
        answerChallenge(activityId, question, index)
        play({ type: "ANSWER", index, correctIndex: question.correct_index as number })
    }
    return (
        <CorrectionChallenge
            state={
                question == null
                    ? { phase: "done", right: game.right, total: questions.length, bestStreak: game.bestStreak }
                    : {
                          phase: "question",
                          question: {
                              question: statement,
                              textFormat: quizTextFormat(statement),
                              choices,
                              choiceFormats: choices.map(quizTextFormat),
                              correctIndex: question.correct_index as number,
                          },
                          index: game.index,
                          total: questions.length,
                          selected: game.selected,
                          streak: game.streak,
                      }
            }
            onAnswer={onAnswer}
            onNext={() => play({ type: "NEXT" })}
        />
    )
}

/** Une pause : la phrase montrée est ici ; le souffle de la pause zen, lui, est au design system. */
function Pause({ kind }: { kind: "breathing" | "tongue" | "fact" }) {
    const [index, setIndex] = useState(0)
    if (kind === "tongue") {
        return (
            <CorrectionPause
                kind="tongue"
                phrase={VIRELANGUES[index % VIRELANGUES.length] as string}
                onNext={() => setIndex((i) => i + 1)}
            />
        )
    }
    if (kind === "fact") {
        const fact = FACTS[index % FACTS.length] as { label: string; text: string }
        return <CorrectionPause kind="fact" label={fact.label} text={fact.text} onNext={() => setIndex((i) => i + 1)} />
    }
    return <CorrectionPause kind="breathing" />
}

export function StudentActivityCorrection(props: StudentActivityCorrectionProps) {
    const { activity, questions, onDone, onLeftUnsettled } = props
    const wait = useCorrectionWait(activity, { onLeftUnsettled })
    const series = useMemo(() => playableQuestions(questions as ApiChallengeQuestion[]), [questions])
    // Une seule forme par copie, tirée une fois.
    const [kind] = useState(() => pickCorrectionGame(series))

    const work = useMemo(
        () =>
            toCorrectedWork({
                image: wait.correction.image,
                points: wait.correction.points,
                workImages: wait.correction.workImages ?? submittedWorkImages(activity),
            }),
        [wait.correction, activity]
    )

    return (
        <StudentCorrectionPage
            step={wait.step}
            game={
                kind === "quiz" ? (
                    <Challenge questions={series} activityId={activity.id ?? null} />
                ) : (
                    <Pause kind={kind} />
                )
            }
            endingText={wait.endingText ?? undefined}
            onSkip={() => {
                trackCorrectionSkipped()
                onDone()
            }}
            work={work}
            feedback={wait.feedback}
            onFeedback={wait.submitFeedback}
            feedbackSubmitting={wait.feedbackSubmitting}
            feedbackError={correctionFeedbackError(wait.feedbackFailure)}
            onUnderstood={onDone}
        />
    )
}
